import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button, Card, Spinner, Row, Col, Modal, Table } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { AGCDynamicSelect } from "../../generic-components/AGCDynamicSelect";
import { apiGenericCreate, genericSelectGetAll, saveExistingImage, getAllOrgSettings } from "../generic/crud";
import { useVehicleCapture } from "../../hooks/useVehicleCapture";
import { WeighbridgeCaptureModal } from "../../components/weighbridge/WeighbridgeCaptureModal";
import { ENTITY_ID_RAW_PURCHASE, ENTITY_ID_SUPPLIER, ENTITY_ID_INVENTORY_ITEM } from "../generic/Constants";
import { defaultRawPurchase } from "../generic/InitialStates";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";
import { useAuth } from "../../modules/auth";
import { LoaderSlip } from "../sale/LoaderSlip";

// Normalize vehicle number: uppercase, strip whitespace
const normalizeVehicleNumber = (value) => {
    if (!value) return "";
    return value.toUpperCase().replace(/\s+/g, "");
};

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

const CHIP_THRESHOLD = 10;

export function PurchaseEntryStep1Form() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [showLoaderSlip, setShowLoaderSlip] = useState(false);
    const [loaderSlipData, setLoaderSlipData] = useState(null);

    // Inventory items state
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(true);
    const [showProductModal, setShowProductModal] = useState(false);
    const [productSearchText, setProductSearchText] = useState("");

    const { capture, isCapturing, weight: weightTrack, image: imageTrack, groupSelectModal } = useVehicleCapture(true);

    // Org settings for hardware control
    const [weighbridgeEnabled, setWeighbridgeEnabled] = useState(true);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    const [allowPurchaseCorrection, setAllowPurchaseCorrection] = useState(false);

    // Refs
    const vehicleInputRef = useRef(null);

    // Get default form data
    const getDefaultFormData = useCallback(() => {
        return {
            ...defaultRawPurchase,
            purchasePersonId: currentUser ? {
                id: currentUser.id || currentUser._id,
                name: currentUser.name || currentUser.fullName || currentUser.userName || currentUser.email
            } : null,
            isPartialRecord: true,
            purchaseType: "TWO_STEP",
        };
    }, [currentUser]);

    const [formData] = useState(getDefaultFormData);

    // Validation schema - minimal for Step 1
    // loadedWeight is conditionally required (only for weight-based items) — checked in onSubmit
    const validationSchema = Yup.object({
        vehicleNumber: Yup.string().required("Vehicle number is required").min(4, "Vehicle number must be at least 4 characters"),
        supplier: Yup.object().nullable().required("Supplier is required"),
        selectedProduct: Yup.object().nullable().required("Product is required"),
        loadedWeight: Yup.number().nullable().positive("Must be positive"),
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            ...formData,
            vehicleNumber: "",
            selectedProduct: null,
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const normalizedVehicle = normalizeVehicleNumber(values.vehicleNumber);
            if (!normalizedVehicle || normalizedVehicle.length < 4) {
                showToast("Please enter a valid Vehicle Number", TOAST_FAILURE);
                return;
            }
            if (!values.supplier) {
                showToast("Please select a Supplier", TOAST_FAILURE);
                return;
            }
            if (!values.selectedProduct) {
                showToast("Please select a Product", TOAST_FAILURE);
                return;
            }

            // Check if item is weight-based
            const isWeightBased = !values.selectedProduct.unitSymbol || values.selectedProduct.unitSymbol.toLowerCase() === 'ton';

            if (isWeightBased && !values.loadedWeight) {
                showToast(weighbridgeEnabled ? "Please capture loaded weight before saving" : "Please enter loaded weight before saving", TOAST_FAILURE);
                return;
            }
            if (cameraEnabled && !capturedImage) {
                showToast("Please capture vehicle image before saving", TOAST_FAILURE);
                return;
            }

            setIsSaving(true);
            try {
                // Prepare data for submission - matches RawPurchase schema
                const submitData = {
                    purchaseTicketNumber: "", // server will assign at step 2
                    purchaseDate: null, // will be set at step 2
                    purchaseFirstStepDate: new Date().toISOString(),
                    purchaseSecondStepDate: null,
                    vehicleNumber: normalizedVehicle,
                    loadedWeight: isWeightBased ? values.loadedWeight : null,
                    loadedWeightCorrection: isWeightBased ? (parseFloat(values.loadedWeightCorrection) || 0) : 0,
                    emptyWeight: null,
                    driverName: "",
                    battaAmount: 0,
                    inspectionNotes: "",
                    remarks: "",
                    roundOffAmount: 0,
                    dumpLocation: "", // will be set at step 2
                    isPartialRecord: true,
                    purchaseType: "TWO_STEP",
                    supplier: values.supplier ? {
                        id: values.supplier.id || values.supplier.value,
                        name: values.supplier.name || values.supplier.label
                    } : null,
                    purchasePersonId: typeof values.purchasePersonId === 'object' && values.purchasePersonId !== null
                        ? (values.purchasePersonId?.id || values.purchasePersonId?._id || values.purchasePersonId?.value || "")
                        : (values.purchasePersonId || ""),
                    itemsList: values.selectedProduct ? [{
                        id: values.selectedProduct.id || values.selectedProduct.value,
                        inventoryItemName: values.selectedProduct.name || values.selectedProduct.label,
                        itemType: 'INVENTORY',
                        quantity: 0,
                        discountAmount: 0,
                        price: values.selectedProduct.price || 0,
                        unitSymbol: values.selectedProduct.unitSymbol || '',
                        taxPlans: values.selectedProduct.taxPlans || [],
                        hsnCode: values.selectedProduct.hsnCode || '',
                    }] : [],
                };

                const response = await apiGenericCreate(ENTITY_ID_RAW_PURCHASE, submitData);

                // Save captured image to CapturedImage collection
                const responseData = response?.data;
                const newPurchaseId = responseData?.id || responseData?._id || responseData?.data?.id || responseData?.data?._id;
                const newPurchasePrimaryId = responseData?.primaryId || responseData?.data?.primaryId || newPurchaseId;

                if (capturedImage?.base64Image && newPurchasePrimaryId) {
                    try {
                        await saveExistingImage(
                            'PURCHASE',
                            newPurchasePrimaryId,
                            'LOADED_WEIGHT',
                            capturedImage.base64Image,
                            capturedImage.contentType || 'image/jpeg'
                        );
                    } catch (imgError) {
                        console.error('Failed to save captured image:', imgError);
                    }
                }

                // NOTE: We intentionally skip the vehicle search-or-create here.
                // At Step 1 for purchases we only know the LOADED weight (truck arrives
                // from supplier with goods). The vehicle's empty weight will be captured
                // at Step 2 (in PurchaseEntryForm) after unloading; that's where we
                // update or create the Vehicle record.

                showToast("Vehicle entry saved!", TOAST_SUCCESS);

                // Show Unloader Slip for printing (uses shared LoaderSlip with mode="PURCHASE")
                setLoaderSlipData({
                    date: new Date().toISOString(),
                    vehicleNumber: normalizedVehicle,
                    customerName: values.supplier?.name || values.supplier?.label || "",
                    productName: values.selectedProduct?.name || values.selectedProduct?.label || "",
                    emptyWeight: isWeightBased ? values.loadedWeight : null,
                    correction: isWeightBased ? (parseFloat(values.loadedWeightCorrection) || 0) : 0,
                    operatorName: currentUser?.name || currentUser?.fullName || currentUser?.userName || "",
                });
                setShowLoaderSlip(true);
            } catch (error) {
                showToast("Error saving entry: " + (error.response?.data || error.message), TOAST_FAILURE);
            } finally {
                setIsSaving(false);
            }
        },
    });

    // Load org settings on mount
    useEffect(() => {
        getAllOrgSettings().then((res) => {
            const settings = res?.data || [];
            const getBool = (name, def) => { const s = settings.find(s => s.typeName === name); return s ? (s.value === true || s.value === 'true') : def; };
            setWeighbridgeEnabled(getBool('ENABLE_WEIGHBRIDGE_CAPTURE', true));
            setCameraEnabled(getBool('ENABLE_CAMERA_CAPTURE', true));
            setAllowPurchaseCorrection(getBool('ALLOW_PURCHASE_CORRECTION', false));
        }).catch(() => {});
    }, []);

    // Load inventory items on mount (only INVENTORY type)
    useEffect(() => {
        setLoadingItems(true);
        genericSelectGetAll(ENTITY_ID_INVENTORY_ITEM)
            .then(({ data }) => {
                const mappedData = (data || [])
                    .filter(item => item.inventoryType === 'INVENTORY')
                    .map(item => ({
                        id: item.value || item.id,
                        name: item.label || item.name || item.itemName,
                        inventoryType: item.inventoryType,
                        price: item.price,
                        unitSymbol: item.unitSymbol,
                        taxPlans: item.taxPlan || [],
                        hsnCode: item.hsnCode,
                    }));
                setInventoryItems(mappedData);
                setLoadingItems(false);
            })
            .catch((err) => {
                console.error("Error loading inventory items:", err);
                setInventoryItems([]);
                setLoadingItems(false);
            });
    }, []);

    // Handle product selection
    const handleProductSelect = useCallback((selectedItem) => {
        formik.setFieldValue('selectedProduct', selectedItem);
        setShowProductModal(false);
        setProductSearchText("");
    }, [formik]);

    // Filter inventory items based on search text
    const filteredInventoryItems = inventoryItems.filter(item =>
        item.name?.toLowerCase().includes(productSearchText.toLowerCase())
    );

    // Handle vehicle weight + image capture
    // Respects weighbridgeEnabled and cameraEnabled org settings
    const handleCapture = async () => {
        const wantWeight = isWeightBasedItem && weighbridgeEnabled;
        const wantImage = cameraEnabled;
        if (!wantWeight && !wantImage) return;

        const res = await capture({ weight: wantWeight, image: wantImage });
        if (res.cancelled) return;

        if (res.weight != null) {
            formik.setFieldValue('loadedWeight', res.weight);
            showToast("Weight captured: " + res.weight, TOAST_SUCCESS);
        } else if (wantWeight) {
            showToast(res.weightError?.message || "Failed to capture weight", TOAST_FAILURE);
        }

        if (res.image) {
            setCapturedImage({
                url: `data:${res.image.contentType};base64,${res.image.imageData}`,
                base64Image: res.image.imageData,
                contentType: res.image.contentType,
                timestamp: res.image.timestamp
            });
            if (!wantWeight) {
                showToast("Image captured successfully", TOAST_SUCCESS);
            }
        } else if (wantImage) {
            console.error("Error capturing image:", res.imageError);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        navigate('/purchases-list');
    };

    // Handle vehicle number input change with normalization
    const handleVehicleNumberChange = (e) => {
        const normalized = normalizeVehicleNumber(e.target.value);
        formik.setFieldValue('vehicleNumber', normalized);
    };

    // Focus on vehicle input on mount
    useEffect(() => {
        setTimeout(() => {
            if (vehicleInputRef.current) {
                vehicleInputRef.current.focus();
            }
        }, 100);
    }, []);

    // Ctrl+S keyboard shortcut to save
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                formik.handleSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [formik]);

    // Determine if the selected product is weight-based (Ton) or non-weight (e.g., Cft)
    const isWeightBasedItem = (() => {
        const product = formik.values.selectedProduct;
        if (!product || !product.unitSymbol) return true; // Default to weight-based
        return product.unitSymbol.toLowerCase() === 'ton';
    })();

    const useChips = inventoryItems.length > 0 && inventoryItems.length <= CHIP_THRESHOLD;

    return (
        <>
        <Form onSubmit={formik.handleSubmit}>
            <div className="container-fluid px-0">
                <Row className="justify-content-center">
                    <Col lg={6}>
                        {/* Info Banner */}
                        <div className="alert alert-light border d-flex align-items-center mb-3 py-2" role="alert">
                            <i className="bi bi-info-circle me-2 text-muted"></i>
                            <small className="text-muted">
                                {isWeightBasedItem
                                    ? (weighbridgeEnabled
                                        ? 'Step 1 — Weigh loaded truck, complete purchase later from Pending Purchases.'
                                        : 'Step 1 — Enter loaded weight manually, complete purchase later from Pending Purchases.')
                                    : (cameraEnabled
                                        ? 'Step 1 — Capture vehicle image, complete purchase later from Pending Purchases.'
                                        : 'Step 1 — Record vehicle entry, complete purchase later from Pending Purchases.')
                                }
                                {' '}<kbd>Ctrl+S</kbd> to save.
                            </small>
                        </div>

                        {/* Main Entry Card */}
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <Card.Header className="py-3 px-4" style={{ borderRadius: '12px 12px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-truck me-2 fs-5" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold" style={{ color: '#4a90a4' }}>Vehicle Entry</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {/* Vehicle Number - Free Text Input (first, auto-focus) */}
                                <Form.Group className="mb-4">
                                    <Form.Label className="required">Vehicle Number</Form.Label>
                                    <Form.Control
                                        ref={vehicleInputRef}
                                        type="text"
                                        placeholder="e.g. KA01AB1234"
                                        value={formik.values.vehicleNumber}
                                        onChange={handleVehicleNumberChange}
                                        onBlur={formik.handleBlur}
                                        name="vehicleNumber"
                                        isInvalid={formik.touched.vehicleNumber && !!formik.errors.vehicleNumber}
                                        style={{ fontSize: '1.25rem', fontWeight: 'bold', letterSpacing: '1px' }}
                                        autoComplete="off"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.vehicleNumber}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* Supplier Selection */}
                                <Form.Group className="mb-4">
                                    <AGCDynamicSelect
                                        entityId={ENTITY_ID_SUPPLIER}
                                        formik={formik}
                                        name="Supplier"
                                        id="supplier"
                                        value={formik.values.supplier}
                                        toolTip="Select supplier"
                                        required={true}
                                        searchField="name"
                                    />
                                </Form.Group>

                                {/* Product Selection - chips or modal */}
                                <Form.Group className="mb-4">
                                    <Form.Label className="required">Product</Form.Label>
                                    {loadingItems ? (
                                        <div className="text-center py-2">
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            <small className="text-muted">Loading products...</small>
                                        </div>
                                    ) : useChips ? (
                                        /* Inline chip picker for <= 10 items */
                                        <div className="d-flex flex-wrap gap-2">
                                            {inventoryItems.map((item) => (
                                                <Button
                                                    key={item.id}
                                                    variant={formik.values.selectedProduct?.id === item.id ? "primary" : "outline-secondary"}
                                                    size="sm"
                                                    onClick={() => handleProductSelect(item)}
                                                    className="rounded-pill"
                                                    style={{ fontSize: '0.85rem', border: '1px solid #dee2e6' }}
                                                >
                                                    {item.name}
                                                    {item.unitSymbol ? ` (${item.unitSymbol})` : ''}
                                                    {item.price ? ` - ${formatCurrency(item.price)}` : ''}
                                                </Button>
                                            ))}
                                        </div>
                                    ) : (
                                        /* Modal fallback for > 10 items */
                                        <div
                                            className="form-control d-flex justify-content-between align-items-center"
                                            style={{ cursor: 'pointer', minHeight: '38px' }}
                                            onClick={() => setShowProductModal(true)}
                                        >
                                            {formik.values.selectedProduct ? (
                                                <span className="fw-semibold text-primary">
                                                    {formik.values.selectedProduct.name}
                                                    {formik.values.selectedProduct.price && (
                                                        <span className="text-success ms-2">
                                                            {formatCurrency(formik.values.selectedProduct.price)}
                                                        </span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-muted">Click to select product...</span>
                                            )}
                                            <i className="bi bi-chevron-down text-muted"></i>
                                        </div>
                                    )}
                                    {formik.touched.selectedProduct && formik.errors.selectedProduct && (
                                        <div className="text-danger small mt-1">{formik.errors.selectedProduct}</div>
                                    )}
                                </Form.Group>

                                {/* Weight / Image Capture Section */}
                                <Card className="mb-4" style={{ backgroundColor: '#f8f9fa', border: '1px dashed #dee2e6' }}>
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className="text-muted fw-semibold">
                                                <i className={`bi ${isWeightBasedItem ? (weighbridgeEnabled ? 'bi-speedometer' : 'bi-pencil-square') : 'bi-camera'} me-2`}></i>
                                                {isWeightBasedItem ? 'Loaded Weight' : 'Vehicle Image'}
                                                {isWeightBasedItem && !weighbridgeEnabled && (
                                                    <span className="badge bg-warning bg-opacity-10 text-warning ms-2" style={{ fontSize: '0.65rem' }}>MANUAL</span>
                                                )}
                                            </span>
                                            {(weighbridgeEnabled || cameraEnabled) && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={handleCapture}
                                                    disabled={isCapturing}
                                                >
                                                    {isCapturing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Capturing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-camera me-2"></i>
                                                            {isWeightBasedItem
                                                                ? (weighbridgeEnabled && cameraEnabled ? 'Capture' : cameraEnabled ? 'Capture Image' : 'Capture Weight')
                                                                : 'Capture Image'}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>

                                        {isWeightBasedItem ? (
                                            /* Weight section for Ton items */
                                            <>
                                            {weighbridgeEnabled ? (
                                                /* Auto-capture weight display */
                                                <div className="text-center mb-3">
                                                    <div
                                                        className="p-3 rounded"
                                                        style={{
                                                            backgroundColor: formik.values.loadedWeight ? '#d1e7dd' : '#fff',
                                                            border: formik.values.loadedWeight ? '2px solid #198754' : '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <span className="fw-bold fs-2" style={{ color: formik.values.loadedWeight ? '#198754' : '#6c757d' }}>
                                                            {formik.values.loadedWeight ? `${formik.values.loadedWeight} Ton` : '-- Ton'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Manual weight input */
                                                <div className="mb-3">
                                                    <Form.Control
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        placeholder="Enter loaded weight in Tons"
                                                        value={formik.values.loadedWeight || ''}
                                                        onChange={(e) => formik.setFieldValue('loadedWeight', e.target.value ? parseFloat(e.target.value) : null)}
                                                        style={{
                                                            fontSize: '1.25rem',
                                                            fontWeight: 'bold',
                                                            textAlign: 'center',
                                                            border: formik.values.loadedWeight ? '2px solid #198754' : undefined,
                                                            backgroundColor: formik.values.loadedWeight ? '#d1e7dd' : undefined,
                                                            color: formik.values.loadedWeight ? '#198754' : undefined
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Correction input — for wastage (e.g. stone boulder loss).
                                                Gated by ALLOW_PURCHASE_CORRECTION. Step 1 is always entry-mode,
                                                so we don't need the historical-data fallback used in the 1-step form. */}
                                            {formik.values.loadedWeight > 0 && allowPurchaseCorrection && (() => {
                                                const correction = parseFloat(formik.values.loadedWeightCorrection) || 0;
                                                const effective = parseFloat(formik.values.loadedWeight) - correction;
                                                return (
                                                    <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <Form.Label className="mb-0 text-muted small">
                                                                <i className="bi bi-dash-circle me-1"></i>Correction (wastage)
                                                            </Form.Label>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.001"
                                                                    min="0"
                                                                    placeholder="0"
                                                                    value={formik.values.loadedWeightCorrection || ''}
                                                                    onChange={(e) => {
                                                                        const parsed = e.target.value ? parseFloat(e.target.value) : 0;
                                                                        formik.setFieldValue('loadedWeightCorrection', Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
                                                                    }}
                                                                    onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                                                    style={{ width: '90px', textAlign: 'right' }}
                                                                    size="sm"
                                                                />
                                                                <small className="text-muted">Ton</small>
                                                            </div>
                                                        </div>
                                                        {correction > 0 && (
                                                            <div className="text-end mt-1">
                                                                <small className="text-success fw-semibold">
                                                                    Effective Loaded: {effective.toFixed(3)} Ton
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            </>
                                        ) : (
                                            /* Info message for non-weight items */
                                            !capturedImage && cameraEnabled && (
                                                <div className="text-center mb-3">
                                                    <div className="p-3 rounded" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                                                        <i className="bi bi-camera text-muted fs-3 d-block mb-1"></i>
                                                        <small className="text-muted">
                                                            Capture vehicle image to proceed
                                                        </small>
                                                    </div>
                                                </div>
                                            )
                                        )}

                                        {/* Camera-off info */}
                                        {!cameraEnabled && !capturedImage && (
                                            <div className="text-center p-3 mb-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px dashed #ffc107' }}>
                                                <i className="bi bi-camera-video-off text-warning" style={{ fontSize: '1.5rem' }}></i>
                                                <div className="text-muted small mt-1">Camera capture is disabled</div>
                                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Vehicle image is not required</div>
                                            </div>
                                        )}

                                        {/* Captured Image Preview */}
                                        {capturedImage && (
                                            <CapturedImagePreview src={capturedImage.url} />
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* Action Buttons */}
                                <div className="d-grid gap-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        disabled={isSaving || (isWeightBasedItem ? !formik.values.loadedWeight : (cameraEnabled && !capturedImage))}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        {isSaving && <Spinner animation="border" size="sm" className="me-2" />}
                                        <i className="bi bi-check-lg me-2"></i>
                                        {isWeightBasedItem
                                            ? (formik.values.loadedWeight ? 'Save Entry' : (weighbridgeEnabled ? 'Capture Weight to Save' : 'Enter Weight to Save'))
                                            : (cameraEnabled && !capturedImage ? 'Capture Image to Save' : 'Save Entry')
                                        }
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Loader Slip Modal (purchase mode) */}
            <LoaderSlip
                show={showLoaderSlip}
                slipData={loaderSlipData}
                mode="PURCHASE"
                onClose={() => {
                    setShowLoaderSlip(false);
                    navigate('/pending-purchases');
                }}
            />

            {/* Product Selection Modal (fallback for > 10 items) */}
            <Modal show={showProductModal} onHide={() => { setShowProductModal(false); setProductSearchText(""); }} centered>
                <Modal.Header closeButton className="bg-primary bg-opacity-10">
                    <Modal.Title className="text-primary">
                        <i className="bi bi-box me-2"></i>
                        Select Product
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="p-3 border-bottom">
                        <Form.Control
                            type="text"
                            placeholder="Search products..."
                            value={productSearchText}
                            onChange={(e) => setProductSearchText(e.target.value)}
                            autoFocus
                        />
                        <small className="text-muted">Click on a product to select it</small>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {loadingItems ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading products...
                            </div>
                        ) : (
                            <Table hover className="mb-0">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th className="ps-3">Product Name</th>
                                        <th className="text-end pe-3" style={{ width: '150px' }}>Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInventoryItems.length > 0 ? (
                                        filteredInventoryItems.map((item) => (
                                            <tr
                                                key={item.id}
                                                onClick={() => handleProductSelect(item)}
                                                style={{ cursor: 'pointer' }}
                                                className={formik.values.selectedProduct?.id === item.id ? 'table-primary' : ''}
                                            >
                                                <td className="ps-3 py-3">
                                                    <span className="fw-semibold text-primary">{item.name}</span>
                                                    {item.unitSymbol && (
                                                        <small className="text-muted ms-1">({item.unitSymbol})</small>
                                                    )}
                                                </td>
                                                <td className="text-end pe-3 py-3">
                                                    <span className="text-success fw-semibold">{formatCurrency(item.price)}</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center py-4 text-muted">
                                                {productSearchText ? 'No products match your search' : 'No inventory products available'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowProductModal(false); setProductSearchText(""); }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Form>

        {/* Capture Processing Modal */}
        <WeighbridgeCaptureModal show={isCapturing} weight={weightTrack} image={imageTrack} />
        {groupSelectModal}
        </>
    );
}

// Captured image preview with click-to-enlarge
function CapturedImagePreview({ src }) {
    const [showModal, setShowModal] = useState(false);
    return (
        <div className="mt-3">
            <small className="text-muted d-block mb-2">Captured Image:</small>
            <img
                src={src}
                alt="Captured vehicle"
                className="rounded"
                style={{ width: '100%', height: 'auto', cursor: 'pointer' }}
                onClick={() => setShowModal(true)}
            />
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className="py-2">
                    <Modal.Title style={{ fontSize: '14px' }}>
                        <i className="bi bi-image me-2"></i>Loaded Weight Image
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <img src={src} alt="Captured vehicle" style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Modal.Body>
            </Modal>
        </div>
    );
}
